import { fetchJSON as fetchAPI } from './client';

export type IntegrationDashboard = {
  tenant_id: string;
  workspace_id?: string;
  total_connectors: number;
  active_connectors: number;
  failed_connectors: number;
  catalog_size: number;
  pending_sync_jobs: number;
  running_sync_jobs: number;
  webhook_endpoints: number;
  webhook_failures_24h: number;
  credentials_expiring: number;
  avg_latency_ms: number;
  events_published_24h: number;
  unread_notifications: number;
  by_category?: Record<string, number>;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ConnectorCatalogEntry = {
  id: string;
  provider: string;
  name: string;
  version: string;
  category: string;
  description?: string;
  auth_type: string;
  supported_features?: string[];
  rate_limit_rps?: number;
  featured?: boolean;
  canonical_domains?: string[];
};

export type Connector = {
  id: string;
  tenant_id: string;
  workspace_id?: string;
  provider: string;
  name: string;
  version: string;
  status: string;
  base_url?: string;
  rate_limit_rps?: number;
  created_at: string;
  updated_at: string;
};

export type CredentialVaultEntry = {
  connector_id: string;
  connector_name?: string;
  provider: string;
  auth_type: string;
  version: number;
  expires_at?: string;
  last_rotated_at?: string;
  valid: boolean;
  scopes?: string[];
  updated_at: string;
};

export type SyncJobRecord = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  connector_id: string;
  strategy: string;
  domain?: string;
  status: string;
  checkpoint?: string;
  records_synced: number;
  error?: string;
  retry_count: number;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
};

export type WebhookEndpoint = {
  id: string;
  connector_id: string;
  url: string;
  events?: string[];
  tenant_id: string;
  active: boolean;
  delivery_count: number;
  failure_count: number;
};

export type ProviderHealthStatus = {
  connector_id: string;
  provider: string;
  state: string;
  latency_ms?: number;
  error_rate?: number;
  throughput_rps?: number;
  rate_limit_used_pct?: number;
  last_sync_at?: string;
  checked_at: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type IntegrationNotification = {
  id: string;
  title: string;
  body?: string;
  severity?: string;
  read: boolean;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_INTEGRATION_API ?? '/api/v1/integration';
const ROOT_API = (import.meta.env.VITE_INTEGRATION_API ?? '/api/v1/integration').replace(/\/integration$/, '') || '/api/v1';

export type APIKey = {
  id: string;
  tenant_id: string;
  name: string;
  key_prefix: string;
  scopes?: string[];
  rate_limit_rps?: number;
  status: string;
  created_at: string;
};

export type APIKeyWithSecret = APIKey & { secret: string };

export type WebhookSubscription = {
  id: string;
  tenant_id: string;
  connector_id?: string;
  url: string;
  events?: string[];
  active: boolean;
  created_at: string;
};

export type GatewayDashboard = {
  tenant_id: string;
  routes: { id: string; path: string; method: string; auth_type: string; rate_limit_rps: number }[];
  api_keys: number;
  webhooks: number;
  connectors: number;
  platform_health?: { module: string; status: string; latency_ms?: number }[];
  generated_at: string;
};

export type DeveloperPortalSummary = {
  tenant_id: string;
  api_keys: number;
  connectors: number;
  catalog_size: number;
  webhook_count: number;
  openapi_spec_url: string;
  generated_at: string;
};

export type RegisteredService = {
  id: string;
  tenant_id?: string;
  name: string;
  version: string;
  owner: string;
  health_status: string;
  dependencies?: string[];
  capabilities?: string[];
  authentication_method: string;
  supported_apis?: string[];
  supported_events?: string[];
  sla: string;
  lifecycle_status: string;
  port?: number;
  api_prefix?: string;
  product_family?: string;
  integration_types?: string[];
  registered_at: string;
};

export type IntegrationContract = {
  id: string;
  tenant_id?: string;
  name: string;
  type: string;
  version: string;
  schema_version: string;
  producer_service: string;
  consumer_services?: string[];
  backward_compatible: boolean;
  breaking_change_detected: boolean;
  compatibility_status: string;
  description?: string;
  published_at: string;
};

export type IntegrationPolicy = {
  id: string;
  name: string;
  policy_type: string;
  retry_max?: number;
  timeout_ms?: number;
  circuit_threshold?: number;
  rate_limit_rps?: number;
  auth_policy?: string;
  version_policy?: string;
  idempotency_required: boolean;
  error_mapping?: string;
  applies_to?: string[];
  enabled: boolean;
};

export type DependencyEdge = {
  from: string;
  to: string;
  relation: string;
  critical: boolean;
};

export type DependencyGraph = {
  tenant_id: string;
  services: string[];
  edges: DependencyEdge[];
  circular_dependencies?: string[];
};

export type ContractValidationResult = {
  valid: boolean;
  compatibility_status: string;
  breaking_changes?: string[];
  consumer_impact?: string[];
  message: string;
};

export type IntegrationPlatformHealth = {
  tenant_id: string;
  integration_health_score: number;
  contract_compliance: number;
  api_compatibility: number;
  dependency_stability: number;
  avg_integration_latency_ms: number;
  service_availability: number;
  registered_services: number;
  active_contracts: number;
  breaking_changes: number;
  deprecated_services: number;
  validated_at: string;
};

export type VersionMatrixEntry = {
  service: string;
  contract_name: string;
  current_version: string;
  min_consumer_version: string;
  status: string;
};

export type IntegrationGovernanceDashboard = {
  tenant_id: string;
  services: RegisteredService[];
  contracts: IntegrationContract[];
  health: IntegrationPlatformHealth;
  policies: IntegrationPolicy[];
  dependencies: DependencyGraph;
  version_matrix: VersionMatrixEntry[];
};

export type ConnectorTestResult = {
  success: boolean;
  connector_id?: string;
  provider?: string;
  latency_ms: number;
  status_code?: number;
  message?: string;
  tested_at: string;
};

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

function fetchRoot<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(ROOT_API, path, init);
}

export const integrationApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<IntegrationDashboard>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  catalog: (category?: string, search?: string) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    const qs = params.toString();
    return fetchJSON<ConnectorCatalogEntry[]>(`/catalog${qs ? `?${qs}` : ''}`);
  },

  connectionWizard: (body: {
    workspace_id: string;
    catalog_id: string;
    name?: string;
    base_url?: string;
    configuration?: Record<string, string>;
  }) => fetchJSON<Connector>('/connections/wizard', { method: 'POST', body: JSON.stringify(body) }),

  listConnections: (workspaceId?: string) =>
    fetchJSON<Connector[]>(`/connections${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  credentialVault: () => fetchJSON<CredentialVaultEntry[]>('/credentials/vault'),

  rotateCredential: (connectorId: string, credentials: string) =>
    fetchJSON<{ status: string }>(`/credentials/${connectorId}/rotate`, {
      method: 'POST',
      body: JSON.stringify({ credentials }),
    }),

  createSyncJob: (body: {
    workspace_id: string;
    connector_id: string;
    strategy?: string;
    domain?: string;
  }) => fetchJSON<SyncJobRecord>('/sync/jobs', { method: 'POST', body: JSON.stringify(body) }),

  listSyncJobs: (workspaceId?: string) =>
    fetchJSON<SyncJobRecord[]>(`/sync/jobs${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  runSyncJob: (id: string) => fetchJSON<SyncJobRecord>(`/sync/jobs/${id}/run`, { method: 'POST' }),

  webhooks: (workspaceId?: string) =>
    fetchJSON<WebhookEndpoint[]>(`/webhooks${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  health: (workspaceId?: string) =>
    fetchJSON<ProviderHealthStatus[]>(`/health${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listActivity: (limit = 20) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),

  listNotifications: (unread = true) => fetchJSON<IntegrationNotification[]>(`/notifications?unread=${unread}`),

  markNotificationRead: (id: string) =>
    fetchJSON<{ status: string }>(`/notifications/${id}/read`, { method: 'POST' }),

  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),

  testConnector: (body: { connector_id?: string; catalog_id?: string }) =>
    fetchRoot<ConnectorTestResult>('/connectors/test', { method: 'POST', body: JSON.stringify(body) }),

  listAPIKeys: () => fetchRoot<APIKey[]>('/api-keys'),

  createAPIKey: (body: { name: string; scopes?: string[]; rate_limit_rps?: number }) =>
    fetchRoot<APIKeyWithSecret>('/api-keys', { method: 'POST', body: JSON.stringify(body) }),

  listWebhookSubscriptions: (workspaceId?: string) =>
    fetchRoot<WebhookSubscription[]>(`/webhooks${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createWebhookSubscription: (body: { url: string; connector_id?: string; events?: string[] }) =>
    fetchRoot<WebhookSubscription>('/webhooks', { method: 'POST', body: JSON.stringify(body) }),

  listIntegrationJobs: (workspaceId?: string) =>
    fetchJSON<SyncJobRecord[]>(`/jobs${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  gatewayDashboard: (workspaceId?: string) =>
    fetchJSON<GatewayDashboard>(`/gateway${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  developerPortal: () => fetchJSON<DeveloperPortalSummary>('/developer'),

  platformHealth: () => fetchJSON<{ module: string; status: string; latency_ms?: number }[]>('/platform-health'),
};

export const integrationGovernanceApi = {
  listServices: () => fetchJSON<RegisteredService[]>('/services'),

  listContracts: () => fetchJSON<IntegrationContract[]>('/contracts'),

  validateContract: (body: { contract_id?: string; producer_service?: string; consumer_service?: string; version?: string }) =>
    fetchJSON<ContractValidationResult>('/contracts/validate', { method: 'POST', body: JSON.stringify(body) }),

  dependencies: () => fetchJSON<DependencyGraph>('/dependencies'),

  listPolicies: () => fetchJSON<IntegrationPolicy[]>('/policies'),

  registryHealth: () => fetchJSON<IntegrationPlatformHealth>('/registry/health'),

  versionMatrix: () => fetchJSON<VersionMatrixEntry[]>('/version-matrix'),

  dashboard: () => fetchJSON<IntegrationGovernanceDashboard>('/governance/dashboard'),
};

export const CONNECTOR_CATEGORIES = [
  'marketplace',
  'advertising',
  'logistics',
  'erp',
  'accounting',
  'payment',
  'government',
  'crm',
  'communication',
  'storage',
  'identity',
  'bi',
  'iot',
  'custom',
] as const;
